import {
  generateVideoThumbnails,
  importFileandPreview,
} from "@rajesh896/video-thumbnails-generator";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { extractMediaUrls } from "../../utils";
import { allowedSelectCoverPlatforms } from "./const";
import { UseVideoCoverProps } from "./types";
import { convertVideoUrlToBlob } from "./utils";
import Bugsnag from "@bugsnag/js";

export const useVideoCover = ({
  platformPost,
  selectedPlatform,
  platforms,
  thumbCount = 10,
}: UseVideoCoverProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [videoMedia, setVideoMedia] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [videoLoadingError, setVideoLoadingError] = useState(false);

  const mediaFiles = useMemo(() => {
    const { mediaFiles, isVideo, url } = platformPost;

    if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
      return mediaFiles;
    }

    if (isVideo && url) {
      return [
        {
          isVideo,
          url,
        },
      ];
    }

    return [];
  }, [platformPost]);

  const isVideoPost =
    mediaFiles.length === 1 && mediaFiles[0]?.isVideo === true;

  const showVideoSelector = useMemo(() => {
    const { isStory, storyType } = platformPost;
    const mediaUrls = extractMediaUrls(platformPost) || [];

    // Return false for edge cases: multiple media, image posts, or non-reel stories
    if (
      mediaUrls.length > 1 || // for more than 1 assets
      (mediaUrls.length > 0 && !isVideoPost) || // for image assets
      (isStory && storyType !== "reel")
    ) {
      return false;
    }

    // Return false if no video post (this handles the no media case)
    if (!isVideoPost) {
      return false;
    }

    if (selectedPlatform === "LINKED") {
      return platforms.some((platform) =>
        allowedSelectCoverPlatforms.includes(platform.TYPE)
      );
    }

    return allowedSelectCoverPlatforms.includes(selectedPlatform);
  }, [platformPost, selectedPlatform, platforms, isVideoPost]);

  const { url, thumbnailUrl, lowQualityVideo } = mediaFiles?.[0] || {};
  const originalMediaUrl = url;
  const lowQualityMediaUrl = lowQualityVideo?.url;
  const mediaUrl = lowQualityMediaUrl || originalMediaUrl;

  useEffect(() => {
    if (!mediaUrl || !showVideoSelector) {
      if (videoUrl) {
        setVideoUrl("");
        setThumbnails([]);
        setVideoMedia(null);
        setVideoLoadingError(false);
      }
      return;
    }

    const init = async () => {
      setIsLoading(true);

      try {
        const video = await convertVideoUrlToBlob(mediaUrl);

        const res = await importFileandPreview(video as File);

        try {
          const thumbs = await generateVideoThumbnails(
            video as File,
            thumbCount,
            "jpg"
          );
          setThumbnails(thumbs);
        } catch (error) {
          // Handle errors in thumbnail generation
          // we log the error and notify Bugsnag
          // but we don't stop the video from being set
          console.error("Error generating video thumbnails:", error);
          if (error instanceof Error) {
            Bugsnag.notify(error, (event) => {
              event.addMetadata("videoCoverSelector", {
                mediaUrl,
                showVideoSelector,
              });
            });
          }
          toast.error(
            "Fail to generate video thumbnails for this video, please try again later."
          );
        }

        setVideoMedia(video as File);

        setVideoUrl(res);
      } catch (error) {
        console.log(error);
        setVideoLoadingError(true);
        if (error instanceof Error) {
          Bugsnag.notify(error, (event) => {
            event.addMetadata("videoCoverSelector", {
              mediaUrl,
              showVideoSelector,
            });
          });
        }
        toast.error(
          "Fail to load the video, please remove the video and try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl, showVideoSelector]);

  return {
    showVideoSelector,
    isLoading,
    videoMedia,
    videoUrl,
    thumbnails,
    thumbnailUrl,
    videoLoadingError,
    originalMediaUrl,
  };
};
